import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Card, CardBody, CardFooter, CardHeader, Col, Row } from "reactstrap";
import { useService } from "../../context/ServiceContext";
import LoadedContent from "../LoadedContent";
import { useAccessory } from "../../context/AccessoryContext";
import AccessoryProgress from "./AccessoryProgress";
import { fireAlert, fireConfirm } from "../../utils/alerts";

function AccessorySummary({ onPrevious, progressItems, history }) {
  const intl = useIntl();
  const { accessorySpecificationStore } = useService();
  const { accessory, accessories, updateAccessories, isNewAccessory, accessoriesAllValid, getErrors, newAccessory, invalidateSession } = useAccessory();
  const [saving, setSaving] = useState(false);
  const [saveEnabled, setSaveEnabled] = useState(false);
  const [errors, setErrors] = useState(false);

  useEffect(() => {
    checkSaveEnabled();
  }, []);

  async function checkSaveEnabled() {
    setSaveEnabled(await accessoriesAllValid());
  }

  async function submitAccessories() {
    setSaving(true);

    try {
      const response = await accessorySpecificationStore.store(accessories);

      let clonedAccessories = [...accessories];
      updateAccessories(clonedAccessories, true);

      if (response.result == false) {
        fireAlert("Something went wrong", intl.formatMessage({ id: "app.error", defaultMessage: "Error" }), "error");
      } else {
        fireAlert(
          intl.formatMessage({ id: "accessory_summary.accessory_saved", defaultMessage: "Accessory successfully saved." }),
          intl.formatMessage({ id: "app.success", defaultMessage: "Success" }),
          "success"
        ).then(() => {
          invalidateSession();
          history.push("/accessories");
        });
      }
    } catch (error) {
      fireAlert("Something went wrong", intl.formatMessage({ id: "app.error", defaultMessage: "Error" }), "error");
    }

    setSaving(false);
  }

  return (
    <LoadedContent loading={saving} overlay={true}>
      <Row>
        <Col xl={3} className="d-sm-none d-xl-block">
          <AccessoryProgress onPrevious={onPrevious} items={progressItems}></AccessoryProgress>
        </Col>
        <Col xl={9}>
          <Card>
            <CardHeader>
              <FormattedMessage id="product_information.finish" defaultMessage="Please check the summary below" />
            </CardHeader>
            <CardBody>
              <table className="table align-items-center">
                <thead className="thead-light">
                  <tr>
                    <th style={{ width: 80 }}>
                      <FormattedMessage id="product_information.column_status" defaultMessage="Status" />
                    </th>
                    <th>
                      <FormattedMessage id="product_information.column_codProductoAccessorio" defaultMessage="Accessorio code" />
                    </th>
                    <th>
                      <FormattedMessage id="product_information.column_productoAccessorio" defaultMessage="Accessorio description" />
                    </th>
                    <th>
                      <FormattedMessage id="product_information.column_series" defaultMessage="Series" />
                    </th>
                    <th>
                      <FormattedMessage id="product_information.column_company" defaultMessage="Company" />
                    </th>
                    <th style={{ width: 120 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {accessories.map((a, i) => (
                    <SummaryRow accessory={a} getErrors={getErrors} key={i}></SummaryRow>
                  ))}
                </tbody>
              </table>
            </CardBody>
            <CardFooter>
              <button className="btn btn-outline-danger" onClick={submitAccessories} disabled={!saveEnabled}>
                <FormattedMessage id="app.save" defaultMessage="Save" />
              </button>
            </CardFooter>
          </Card>
        </Col>
      </Row>
    </LoadedContent>
  );
}

function SummaryRow({ accessory, getErrors }) {
  const [errors, setErrors] = useState(false);
  const intl = useIntl();

  useEffect(() => {
    const check = async () => {
      const err = await getErrors(accessory);
      setErrors(err);
    };
    check();
  }, [accessory]);

  return (
    <>
      <tr>
        <td>
          {errors?.hasErrors ? (
            <i
              className="text-danger fas fa-2x fa-minus-circle"
              onClick={() => fireAlert(<ErrorSummary intl={intl} errors={errors}></ErrorSummary>, "Incorrect attributes", "error")}
            ></i>
          ) : (
            <i className="text-success fas fa-2x fa-check-circle"></i>
          )}
        </td>
        <td>{accessory.codAccesorioComercial}</td>
        <td>{accessory.productoAccesorioComercial}</td>
        <td>{accessory.series?.name}</td>
        <td>{accessory.idCompanyManufacturer}</td>
      </tr>

      {accessory.result !== undefined ? (
        <tr>
          <td className={`text-white text-center bg-${accessory.result ? "success" : "danger"}`} colSpan="6">
            {accessory.result ? (
              <>
                <i className="fas fa-check"></i> <FormattedMessage id="product_summary.product_saved_to_database" defaultMessage="Product saved to database" />
              </>
            ) : (
              <>
                <i className="fas fa-minus"></i> <FormattedMessage id="product_summary.product_save_error" defaultMessage="A problem occurred saving this product" />
              </>
            )}
          </td>
        </tr>
      ) : (
        <tr>
          <td className="p-0 bg-dark" style={{ height: 2 }} colSpan="6"></td>
        </tr>
      )}
    </>
  );
}

export function ErrorSummary({ errors, intl }) {
  const ignore = ["hasErrors"];
  const errorKeys = Object.keys(errors).filter((k) => !ignore.includes(k));
  return (
    <div className="text-left">
      {errors && <b>{intl.formatMessage({ id: "app.accessory", defaultMessage: "Accessory" })}</b>}
      <ul className="list-unstyled">
        <li>
          <ul>
            {errorKeys.map((e, i) => (
              <li key={i}>
                <b>{intl.formatMessage(errors[e].formattedMessage)}</b>
                <span className="badge text-danger">{intl.formatMessage(errors[e].errorFormattedMessage)}</span>
              </li>
            ))}
          </ul>
        </li>
      </ul>
    </div>
  );
}

export default AccessorySummary;
